/**
 * Display fields + a deep link for an inbox row. Message rows (conversation
 * messages, replies, mentions, reactions and Space replies / mentions) render
 * through the same copy as their toast, filtered by the notification detail
 * settings, so the history always matches what was shown live.
 */

import type { TFunction } from 'i18next';
import type { InboxItem, NotificationDisplaySettings } from '@adieuu/shared';
import { parseThreadParam } from '../../hooks/threads/useMessageThread';
import type { AppIconName } from '../../icons/appIcons';
import {
  describeConversationNotification,
  describeSpaceNotification,
  type ConversationNotificationKind,
  type NotificationTranslate,
  type SpaceNotificationKind,
} from '../../utils/notificationCopy';

export interface InboxRowDescriptor {
  icon: AppIconName;
  primary: string;
  secondary?: string;
  href?: string;
  /** Open the sidebar invitations panel instead of navigating. */
  openInvites?: boolean;
}

/** Client-side context for message rows; names the server cannot see. */
export interface InboxNotificationLookups {
  display: NotificationDisplaySettings;
  conversationFor: (conversationId: string) => { type?: string; name?: string } | undefined;
  spaceNameFor: (spaceId: string) => string | undefined;
  senderNameFor: (identityId: string) => string | undefined;
}

function str(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

const CONVERSATION_KIND_BY_TYPE: Record<string, ConversationNotificationKind> = {
  conversation_message: 'message',
  conversation_message_reply: 'reply',
  message_reaction: 'reaction',
};

const CONVERSATION_ICON: Record<ConversationNotificationKind, AppIconName> = {
  message: 'message',
  reply: 'reply',
  mention: 'bell',
  reaction: 'smile',
};

const SPACE_KIND_BY_TYPE: Record<string, SpaceNotificationKind> = {
  space_message_mention: 'mention',
  space_message_reply: 'reply',
};

function spaceRow(
  args: {
    kind: SpaceNotificationKind;
    spaceId?: string;
    channelName?: string;
    fromIdentityId?: string;
    href?: string;
  },
  t: TFunction,
  lookups: InboxNotificationLookups,
): InboxRowDescriptor {
  const { title, body } = describeSpaceNotification({
    kind: args.kind,
    senderName: args.fromIdentityId ? lookups.senderNameFor(args.fromIdentityId) : undefined,
    channelName: args.channelName,
    spaceName: args.spaceId ? lookups.spaceNameFor(args.spaceId) : undefined,
    display: lookups.display.spaces,
    t: t as unknown as NotificationTranslate,
  });
  return {
    icon: args.kind === 'reply' ? 'reply' : 'bell',
    primary: body,
    secondary: title,
    href: args.href,
  };
}

/**
 * Unknown notification types degrade to a generic label with no link rather
 * than throwing.
 */
export function describeInboxItem(
  item: InboxItem,
  t: TFunction,
  lookups: InboxNotificationLookups,
): InboxRowDescriptor {
  if (item.kind === 'space_mention') {
    return spaceRow(
      {
        kind: item.mentionType === 'here' ? 'mentionHere' : 'mentionRole',
        spaceId: item.spaceId,
        channelName: item.channelName,
        fromIdentityId: item.fromIdentityId,
        href: `/s/${item.spaceSlug}/c/${item.channelId}`,
      },
      t,
      lookups,
    );
  }

  const data = item.data;
  const spaceSlug = str(data, 'spaceSlug');
  const channelId = str(data, 'channelId');
  const threadRootId = parseThreadParam(str(data, 'threadRootId'));
  const spaceHref =
    spaceSlug && channelId
      ? `/s/${spaceSlug}/c/${channelId}${threadRootId ? `?thread=${threadRootId}` : ''}`
      : undefined;
  const conversationId = str(data, 'conversationId');
  const conversationHref = conversationId
    ? `/conversations/${conversationId}${threadRootId ? `?thread=${threadRootId}` : ''}`
    : undefined;

  const spaceKind = SPACE_KIND_BY_TYPE[item.type];
  if (spaceKind) {
    return spaceRow(
      {
        kind: spaceKind,
        spaceId: str(data, 'spaceId'),
        channelName: str(data, 'channelName'),
        fromIdentityId: str(data, 'fromIdentityId'),
        href: spaceHref,
      },
      t,
      lookups,
    );
  }

  const baseKind = CONVERSATION_KIND_BY_TYPE[item.type];
  if (baseKind) {
    const kind = baseKind === 'message' && data.mentioned === true ? 'mention' : baseKind;
    const fromIdentityId = str(data, 'fromIdentityId');
    const conversation = conversationId ? lookups.conversationFor(conversationId) : undefined;
    const { title, body } = describeConversationNotification({
      kind,
      senderName: fromIdentityId ? lookups.senderNameFor(fromIdentityId) : undefined,
      conversation: { type: conversation?.type, name: conversation?.name },
      display: lookups.display.conversations,
      t: t as unknown as NotificationTranslate,
    });
    return { icon: CONVERSATION_ICON[kind], primary: body, secondary: title, href: conversationHref };
  }

  switch (item.type) {
    case 'friend_request_received':
      return {
        icon: 'userPlus',
        primary: t('notifications.inbox.friendRequest'),
        secondary: str(data, 'fromUsername') && `@${str(data, 'fromUsername')}`,
        href: str(data, 'fromIdentityId') && `/identity/${str(data, 'fromIdentityId')}`,
      };
    case 'friend_request_accepted':
      return {
        icon: 'user',
        primary: t('notifications.inbox.friendAccepted'),
        secondary: str(data, 'byUsername') && `@${str(data, 'byUsername')}`,
        href: str(data, 'byIdentityId') && `/identity/${str(data, 'byIdentityId')}`,
      };
    case 'conversation_created':
      return { icon: 'message', primary: t('notifications.inbox.dm'), href: conversationHref };
    case 'call_incoming':
      return { icon: 'phone', primary: t('notifications.inbox.call'), href: conversationHref };
    case 'group_invite_received':
    case 'group_member_added':
      return { icon: 'group', primary: t('notifications.inbox.groupInvite'), href: conversationHref };
    case 'space_invite_received': {
      const spaceName = str(data, 'spaceName');
      const encrypted = data.encryptIdentity === true;
      return {
        icon: 'spaces',
        primary: t('notifications.inbox.spaceInvite'),
        secondary: spaceName ?? (encrypted ? t('spaces.encryptedSpacePlaceholder') : undefined),
        openInvites: true,
      };
    }
    case 'achievement_unlocked':
      return { icon: 'trophy', primary: t('notifications.inbox.achievement') };
    default:
      return { icon: 'bell', primary: t('notifications.inbox.generic') };
  }
}
